// src/components/common/LoadingSpinner.jsx

import React from "react";
import { cn } from "@/lib/utils";

const LoadingSpinner = ({
  size = "default",
  variant = "primary",
  className,
  label,
  fullScreen = false,
  overlay = false,
}) => {
  const sizeClasses = {
    xs: "h-3 w-3 border",
    sm: "h-4 w-4 border-2",
    default: "h-8 w-8 border-2",
    lg: "h-12 w-12 border-3",
    xl: "h-16 w-16 border-4",
  };

  const variantClasses = {
    primary: "border-primary/30 border-t-primary",
    secondary: "border-secondary/30 border-t-secondary",
    white: "border-white/30 border-t-white",
    success: "border-success/30 border-t-success",
    danger: "border-danger/30 border-t-danger",
  };

  const spinner = (
    <div className={cn("flex flex-col items-center gap-3", className)}>
      <div
        className={cn(
          "animate-spin rounded-full",
          sizeClasses[size],
          variantClasses[variant]
        )}
        role="status"
        aria-label={label || "Loading"}
      />
      {label && (
        <span className="text-sm text-neutral-dark animate-pulse">{label}</span>
      )}
    </div>
  );

  // Full screen loading
  if (fullScreen) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-background">
        {spinner}
      </div>
    );
  }

  // Overlay loading (for sections/cards)
  if (overlay) {
    return (
      <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/80 backdrop-blur-sm rounded-inherit">
        {spinner}
      </div>
    );
  }

  return spinner;
};

// Skeleton loading component for content placeholders
const Skeleton = ({ className, ...props }) => {
  return (
    <div
      className={cn(
        "animate-pulse rounded-md bg-neutral-light",
        className
      )}
      {...props}
    />
  );
};

// Content loader for complex loading states
const ContentLoader = ({ 
  loading, 
  children, 
  skeleton,
  spinnerSize = "default",
  className 
}) => {
  if (loading) {
    if (skeleton) {
      return skeleton;
    }
    return (
      <div className={cn("flex items-center justify-center py-8", className)}>
        <LoadingSpinner size={spinnerSize} />
      </div>
    );
  }
  return children;
};

// Page loader
const PageLoader = ({ label = "Loading..." }) => (
  <LoadingSpinner fullScreen size="lg" label={label} />
);

// Button loader (inline)
const ButtonLoader = ({ className }) => (
  <LoadingSpinner size="xs" variant="white" className={className} />
);

// Table row skeleton
const TableRowSkeleton = ({ columns = 5 }) => (
  <tr className="border-b border-neutral-dark/10">
    {Array.from({ length: columns }).map((_, i) => (
      <td key={i} className="p-4">
        <Skeleton className="h-4 w-full" />
      </td>
    ))}
  </tr>
);

// Card skeleton
const CardSkeleton = ({ className }) => (
  <div className={cn("rounded-xl border border-neutral-dark/10 p-6 space-y-4", className)}>
    <Skeleton className="h-6 w-1/3" />
    <Skeleton className="h-4 w-full" />
    <Skeleton className="h-4 w-2/3" />
    <div className="flex gap-2 pt-2">
      <Skeleton className="h-8 w-20" />
      <Skeleton className="h-8 w-20" />
    </div>
  </div>
);

export default LoadingSpinner;
export { 
  LoadingSpinner, 
  Skeleton, 
  ContentLoader, 
  PageLoader, 
  ButtonLoader,
  TableRowSkeleton,
  CardSkeleton 
};
